/**
 * FeatureOverviewForm - Reusable form for Feature overview fields
 * 
 * Used in:
 * - FeatureDetailModal (edit mode)
 * - FeatureMergeModal (source view + merged form)
 * 
 * Props:
 * - formData: { title, description, user_story, status, priority_level, effort_size, due_date, 
 *               epic_id, release_id, tag_ids, persona_ids, assignee_id, is_blocked, blocker_reason }
 * - onChange: (field, value) => void
 * - isEditing: boolean (controls whether fields are editable)
 * - isDisabled: boolean (overrides isEditing for locked states like in-production)
 * - colors: theme colors from useSafeMode
 * - epics: array of available epics for dropdown
 * - releases: array of available releases for dropdown
 * - personas: array of available personas for selection
 * - teamMembers: array for assignee dropdown
 * - availableTags: array of available tags
 * - statuses: array of status options (default: Backlog, In Progress, Review, Done)
 * - parentDeadlines: array of {date, label} for calendar markers
 * - errors: { [field]: string } - validation errors to display
 * - mode: 'edit' | 'create' | 'view'
 * - onTagAdd: (tagId) => void
 * - onTagRemove: (tagId) => void
 * - onCreateTag: (name, color) => Promise<tag>
 * - currentUserId: string - for blocker tracking
 * - currentUserName: string - for blocker tracking
 */

import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DatePickerWithMarkers } from '../ui/date-picker-with-markers';
import { cn } from '../../lib/utils';
import { Calendar, Users, Tag, AlertTriangle, AlertCircle, X, Plus } from 'lucide-react';
import useFeatureFlags, { FEATURE_IDS } from '../../hooks/useFeatureFlags';

// Status options (default - can be overridden by product's kanban columns)
export const DEFAULT_FEATURE_STATUSES = ['Backlog', 'In Progress', 'Review', 'Done'];

// Effort size options
export const EFFORT_SIZES = ['XS', 'S', 'M', 'L', 'XL'];

// Priority level options
export const PRIORITY_LEVELS = ['Critical', 'High', 'Medium', 'Low'];

// Required fields for feature
export const FEATURE_REQUIRED_FIELDS = ['title'];

// Helper: Get contrast color for tag text
const getContrastColor = (hexColor) => {
  if (!hexColor) return '#ffffff';
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? '#1f2937' : '#ffffff';
};

const FeatureOverviewForm = ({
  formData,
  onChange,
  isEditing = true,
  isDisabled = false,
  colors,
  epics = [],
  releases = [],
  personas = [],
  teamMembers = [],
  availableTags = [],
  statuses = DEFAULT_FEATURE_STATUSES,
  parentDeadlines = [],
  errors = {},
  mode = 'edit',
  onTagAdd,
  onTagRemove,
  onCreateTag,
  currentUserId,
  currentUserName,
}) => {
  const { isEnabled } = useFeatureFlags();
  const showPersonas = isEnabled(FEATURE_IDS.TAB_PERSONAS);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [tagSearch, setTagSearch] = useState('');

  const handleChange = (field, value) => {
    if (onChange) {
      onChange(field, value);
    }
  };

  // Helper to get error styling for a field
  const getFieldErrorClass = (field) => {
    return errors[field] ? 'border-red-500 ring-1 ring-red-500' : '';
  };

  const effectiveDisabled = isDisabled || !isEditing;

  // Filter tags for dropdown
  const filteredTags = availableTags.filter(tag => 
    !formData.tag_ids?.includes(tag.tag_id) &&
    (!tagSearch || tag.name.toLowerCase().includes(tagSearch.toLowerCase()))
  );

  return (
    <div className="space-y-4">
      {/* Title (Full Width) */}
      <div>
        <Label className={cn(colors.textSecondary, errors.title && "text-red-500")}>
          Title <span className="text-red-500">*</span>
        </Label>
        {isEditing && !isDisabled ? (
          <>
            <Input
              value={formData.title || ''}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="e.g., Add user authentication"
              disabled={effectiveDisabled}
              className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary, getFieldErrorClass('title'))}
              data-testid="feature-title-input"
            />
            {errors.title && (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.title}
              </p>
            )}
          </>
        ) : (
          <p className={cn("mt-1 text-lg font-medium", colors.textPrimary)}>
            {formData.title || 'Untitled Feature'}
          </p>
        )}
      </div>

      {/* Tags (Full Width) */}
      <div>
        <Label className={cn("flex items-center gap-1", colors.textSecondary)}>
          <Tag className="w-3 h-3" /> Tags
        </Label>
        <div className={cn(
          "mt-1 flex flex-wrap items-center gap-2 min-h-[38px] p-2 rounded-lg border",
          colors.bgInput, colors.borderPrimary,
          effectiveDisabled && "opacity-70"
        )}>
          {(formData.tag_ids || []).map(tagId => {
            const tag = availableTags.find(t => t.tag_id === tagId);
            return tag ? (
              <span
                key={tagId}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0"
                style={{ 
                  backgroundColor: tag.color,
                  color: getContrastColor(tag.color)
                }}
              >
                {tag.name}
                {isEditing && !isDisabled && onTagRemove && (
                  <button
                    type="button"
                    onClick={() => onTagRemove(tagId)}
                    className="ml-0.5 hover:bg-black/10 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </span>
            ) : null;
          })}
          {isEditing && !isDisabled && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowTagDropdown(!showTagDropdown)}
                className={cn(
                  "inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border border-dashed",
                  "hover:bg-gray-100 dark:hover:bg-gray-700",
                  colors.borderPrimary, colors.textMuted
                )}
              >
                <Plus className="w-3 h-3" />
                Add Tag
              </button>
              {showTagDropdown && (
                <div className={cn(
                  "absolute top-8 left-0 z-50 w-48 max-h-48 overflow-y-auto rounded-lg border shadow-lg",
                  colors.bgCard, colors.borderPrimary
                )}>
                  <div className="p-2 border-b">
                    <Input
                      value={tagSearch}
                      onChange={(e) => setTagSearch(e.target.value)}
                      placeholder="Search tags..."
                      className="h-7 text-xs"
                      autoFocus
                    />
                  </div>
                  {filteredTags.map(tag => (
                    <button
                      key={tag.tag_id}
                      onClick={() => {
                        if (onTagAdd) onTagAdd(tag.tag_id);
                        setShowTagDropdown(false);
                        setTagSearch('');
                      }}
                      className={cn(
                        "w-full flex items-center gap-2 p-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700",
                        colors.textPrimary
                      )}
                    >
                      <span 
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: tag.color }}
                      />
                      {tag.name}
                    </button>
                  ))}
                  {filteredTags.length === 0 && (
                    <p className={cn("p-2 text-xs", colors.textMuted)}>No tags available</p>
                  )}
                </div>
              )}
            </div>
          )}
          {(!formData.tag_ids || formData.tag_ids.length === 0) && effectiveDisabled && (
            <span className={cn("text-sm", colors.textMuted)}>No tags</span>
          )}
        </div>
      </div>

      {/* Epic & Assignee Row */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className={colors.textSecondary}>Epic</Label>
          {isEditing && !isDisabled ? (
            <Select 
              value={formData.epic_id || 'none'} 
              onValueChange={(v) => handleChange('epic_id', v === 'none' ? '' : v)}
              disabled={effectiveDisabled}
            >
              <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary)}>
                <SelectValue placeholder="Select epic" />
              </SelectTrigger>
              <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                <SelectItem value="none">No Epic</SelectItem>
                {epics.map(epic => (
                  <SelectItem key={epic.epic_id} value={epic.epic_id}>{epic.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <p className={cn("mt-1", colors.textPrimary)}>
              {epics.find(e => e.epic_id === formData.epic_id)?.title || 'No Epic'}
            </p>
          )}
        </div>
        <div>
          <Label className={cn("flex items-center gap-1", colors.textSecondary)}>
            <Users className="w-3 h-3" /> Assignee
          </Label>
          {isEditing ? (
            <Select 
              value={formData.assignee_id || 'none'} 
              onValueChange={(v) => handleChange('assignee_id', v === 'none' ? '' : v)}
              disabled={effectiveDisabled}
            >
              <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary)}>
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                <SelectItem value="none">Unassigned</SelectItem>
                {teamMembers.map(m => (
                  <SelectItem key={m.user_id || m.member_id} value={m.user_id || m.member_id}>
                    {m.name || m.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <p className={cn("mt-1", colors.textPrimary)}>
              {teamMembers.find(m => (m.user_id || m.member_id) === formData.assignee_id)?.name || 'Unassigned'}
            </p>
          )}
        </div>
      </div>

      {/* Release & Status Row */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className={colors.textSecondary}>Release</Label>
          {isEditing ? (
            <Select 
              value={formData.release_id || 'parking-lot'} 
              onValueChange={(v) => handleChange('release_id', v === 'parking-lot' ? '' : v)}
              disabled={effectiveDisabled}
            >
              <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary)}>
                <SelectValue placeholder="Parking Lot" />
              </SelectTrigger>
              <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                <SelectItem value="parking-lot">Parking Lot</SelectItem>
                {releases.map(r => (
                  <SelectItem key={r.release_id} value={r.release_id}>{r.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <p className={cn("mt-1", colors.textPrimary)}>
              {releases.find(r => r.release_id === formData.release_id)?.name || 'Parking Lot'}
            </p>
          )}
        </div>
        <div>
          <Label className={colors.textSecondary}>Status</Label>
          {isEditing ? (
            <Select 
              value={formData.status || 'Backlog'} 
              onValueChange={(v) => handleChange('status', v)}
              disabled={effectiveDisabled}
            >
              <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary)}>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                {statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <p className={cn("mt-1", colors.textPrimary)}>{formData.status || 'Backlog'}</p>
          )}
        </div>
      </div>

      {/* Priority & Effort Row */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className={colors.textSecondary}>Priority</Label>
          {isEditing && !isDisabled ? (
            <Select 
              value={formData.priority_level || 'Medium'} 
              onValueChange={(v) => handleChange('priority_level', v)}
              disabled={effectiveDisabled}
            >
              <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary)}>
                <SelectValue placeholder="Select priority" />
              </SelectTrigger>
              <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                {PRIORITY_LEVELS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <p className={cn("mt-1", colors.textPrimary)}>{formData.priority_level || 'Medium'}</p>
          )}
        </div>
        <div>
          <Label className={colors.textSecondary}>Effort</Label>
          {isEditing && !isDisabled ? (
            <Select 
              value={formData.effort_size || 'none'} 
              onValueChange={(v) => handleChange('effort_size', v === 'none' ? '' : v)}
              disabled={effectiveDisabled}
            >
              <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary)}>
                <SelectValue placeholder="Select size" />
              </SelectTrigger>
              <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                <SelectItem value="none">Not set</SelectItem>
                {EFFORT_SIZES.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <p className={cn("mt-1", colors.textPrimary)}>{formData.effort_size || 'Not set'}</p>
          )}
        </div>
      </div>

      {/* Due Date */}
      <div>
        <Label className={cn("flex items-center gap-1", colors.textSecondary)}>
          <Calendar className="w-3 h-3" /> Due Date
        </Label>
        {isEditing && !isDisabled ? (
          <div className="mt-1 max-w-xs">
            <DatePickerWithMarkers
              value={formData.due_date || ''}
              onChange={(date) => handleChange('due_date', date)}
              parentDeadlines={parentDeadlines}
              placeholder="Select due date"
              disabled={effectiveDisabled}
            />
          </div>
        ) : (
          <p className={cn("mt-1", colors.textPrimary)}>
            {formData.due_date ? new Date(formData.due_date).toLocaleDateString() : 'Not set'}
          </p>
        )}
      </div>

      {/* Personas (hidden when personas tab is suppressed) */}
      {showPersonas && (
      <div>
        <Label className={cn("flex items-center gap-1", colors.textSecondary)}>
          <Users className="w-3 h-3" /> Personas
        </Label>
        <div className={cn(
          "mt-1 flex flex-wrap items-center gap-2 min-h-[38px] p-2 rounded-lg border",
          colors.bgInput, colors.borderPrimary,
          effectiveDisabled && "opacity-70"
        )}>
          {(formData.persona_ids || []).map(personaId => {
            const persona = personas.find(p => p.persona_id === personaId);
            return persona ? (
              <span
                key={personaId}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
              >
                {persona.name}
                {isEditing && !isDisabled && (
                  <button
                    type="button"
                    onClick={() => handleChange('persona_ids', formData.persona_ids.filter(id => id !== personaId))}
                    className="ml-0.5 hover:bg-purple-200 dark:hover:bg-purple-800 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </span>
            ) : null;
          })}
          {isEditing && !isDisabled && (
            <Select 
              value=""
              onValueChange={(v) => {
                if (v && !formData.persona_ids?.includes(v)) {
                  handleChange('persona_ids', [...(formData.persona_ids || []), v]);
                }
              }}
            >
              <SelectTrigger className="w-auto min-w-[100px] h-7 text-xs border-0 bg-transparent focus:ring-0 p-0">
                <SelectValue placeholder={formData.persona_ids?.length ? "Add more..." : "Add personas..."} />
              </SelectTrigger>
              <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                {personas
                  .filter(p => !formData.persona_ids?.includes(p.persona_id))
                  .map(p => (
                    <SelectItem key={p.persona_id} value={p.persona_id}>{p.name}</SelectItem>
                  ))
                }
                {personas.filter(p => !formData.persona_ids?.includes(p.persona_id)).length === 0 && (
                  <div className="px-2 py-1.5 text-xs text-gray-500">All personas selected</div>
                )}
              </SelectContent>
            </Select>
          )}
          {(!formData.persona_ids || formData.persona_ids.length === 0) && effectiveDisabled && (
            <span className={cn("text-sm", colors.textMuted)}>No personas linked</span>
          )}
        </div>
      </div>
      )}

      {/* User Story */}
      <div>
        <Label className={colors.textSecondary}>User Story</Label>
        {isEditing && !isDisabled ? (
          <Textarea
            value={formData.user_story || ''}
            onChange={(e) => handleChange('user_story', e.target.value)}
            placeholder="As a [user type], I want to [action] so that [benefit]..."
            rows={3}
            disabled={effectiveDisabled}
            className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
            data-testid="feature-user-story-input"
          />
        ) : (
          <div className={cn("mt-1 p-3 rounded-lg min-h-[60px]", colors.bgTertiary)}>
            <p className={cn("text-sm whitespace-pre-wrap", colors.textPrimary)}>
              {formData.user_story || 'No user story defined'}
            </p>
          </div>
        )}
      </div>

      {/* Description */}
      <div>
        <Label className={colors.textSecondary}>Description</Label>
        {isEditing && !isDisabled ? (
          <Textarea
            value={formData.description || ''}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Additional details about this feature..."
            rows={4}
            disabled={effectiveDisabled}
            className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
            data-testid="feature-description-input"
          />
        ) : (
          <div className={cn("mt-1 p-3 rounded-lg min-h-[80px]", colors.bgTertiary)}>
            <p className={cn("text-sm whitespace-pre-wrap", colors.textPrimary)}>
              {formData.description || 'No description'}
            </p>
          </div>
        )}
      </div>

      {/* Blocked Toggle */}
      <div className={cn(
        "p-4 rounded-lg border",
        formData.is_blocked 
          ? "bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700" 
          : cn(colors.bgTertiary, colors.borderPrimary)
      )}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className={cn("w-5 h-5", formData.is_blocked ? "text-red-500" : colors.textMuted)} />
            <div>
              <Label className={cn("text-sm font-medium", formData.is_blocked ? "text-red-700 dark:text-red-400" : colors.textPrimary)}>
                Blocked
              </Label>
              <p className={cn("text-xs mt-0.5", formData.is_blocked ? "text-red-600 dark:text-red-400" : colors.textMuted)}>
                {formData.is_blocked 
                  ? (formData.blocked_at 
                      ? `Set on ${new Date(formData.blocked_at).toLocaleDateString()}`
                      : 'Feature is currently blocked')
                  : 'Mark this feature as blocked if work cannot proceed'}
              </p>
            </div>
          </div>
          {isEditing && !isDisabled && (
            <Switch
              checked={formData.is_blocked || false}
              onCheckedChange={(checked) => {
                handleChange('is_blocked', checked);
                if (checked) {
                  handleChange('blocked_at', new Date().toISOString());
                  handleChange('blocked_by', currentUserId);
                  handleChange('blocked_by_name', currentUserName);
                } else {
                  handleChange('blocked_at', null);
                  handleChange('blocked_by', null);
                  handleChange('blocked_by_name', null);
                  handleChange('blocker_reason', '');
                }
              }}
              className={formData.is_blocked ? "data-[state=checked]:bg-red-500" : ""}
            />
          )}
        </div>
        
        {formData.is_blocked && (
          <div className="mt-3">
            <Label className={cn("text-xs", "text-red-600 dark:text-red-400")}>Blocker Reason</Label>
            {isEditing && !isDisabled ? (
              <Textarea
                value={formData.blocker_reason || ''}
                onChange={(e) => handleChange('blocker_reason', e.target.value)}
                placeholder="Describe what's blocking this feature..."
                rows={2}
                disabled={effectiveDisabled}
                className={cn(
                  "mt-1 text-sm",
                  "bg-white dark:bg-gray-800 border-red-300 dark:border-red-700",
                  colors.textPrimary
                )}
              />
            ) : (
              <p className={cn("mt-1 text-sm", colors.textPrimary)}>
                {formData.blocker_reason || 'No reason provided'}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeatureOverviewForm;
